import { useI18n } from "../i18n/useI18n";
import type { UiKey } from "../i18n/ui";
import type { OptimizationResult } from "../lib/types";

type Props = { optimization: OptimizationResult };

function effortLabel(effort: "low" | "medium" | "high", t: (k: UiKey) => string) {
  const key = `effort.${effort}` as UiKey;
  return t(key);
}

export function Recommendations({ optimization }: Props) {
  const { t } = useI18n();

  return (
    <div className="rec">
      <div className="cols">
        <div>
          <h2>{t("rec.risks")}</h2>
          {optimization.problems.length === 0 ? (
            <p className="empty">{t("rec.risksEmpty")}</p>
          ) : (
            <ul>
              {optimization.problems.map((p, i) => (
                <li key={i} className={`sev sev-${p.severity}`}>
                  <strong>{p.title}</strong>
                  <span>{p.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h2>{t("rec.suggestions")}</h2>
          <ul>
            {optimization.suggestions.map((s, i) => (
              <li key={i}>
                <strong>{s.title}</strong>
                <span className="impact">{s.impact}</span>
                <span className="effort">
                  {t("rec.effort")} {effortLabel(s.effort, t)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {optimization.architecture.length > 0 && (
        <div className="arch">
          <h2>{t("rec.arch")}</h2>
          <ul>
            {optimization.architecture.map((a, i) => (
              <li key={i}>
                <strong>{a.title}</strong>
                <span>{a.rationale}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <style>{`
        .rec h2 {
          font-size: 0.95rem;
          margin: 0 0 0.65rem;
        }
        .cols {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
        }
        @media (max-width: 640px) {
          .cols {
            grid-template-columns: 1fr;
          }
        }
        ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .cols > div ul li {
          padding: 0.55rem 0;
          border-bottom: 1px solid var(--border);
          font-size: 0.88rem;
        }
        .cols > div ul li strong {
          display: block;
          margin-bottom: 0.2rem;
        }
        .cols > div ul li span {
          display: block;
          color: var(--muted);
        }
        .sev-high strong {
          color: var(--danger);
        }
        .sev-medium strong {
          color: var(--accent);
        }
        .impact {
          margin-top: 0.15rem !important;
        }
        .effort {
          font-size: 0.78rem !important;
          margin-top: 0.15rem !important;
          font-family: var(--mono);
        }
        .empty {
          color: var(--muted);
          font-size: 0.88rem;
          margin: 0;
        }
        .arch {
          margin-top: 1.25rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
        }
        .arch ul li {
          padding: 0.5rem 0;
          font-size: 0.88rem;
        }
        .arch strong {
          display: block;
          margin-bottom: 0.2rem;
        }
        .arch span {
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
